import React, { useState } from "react";
import {
  Alert,
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";

import { supportRequestsRepo } from "data/repositories/supportRequestsRepo";

const CATEGORY_OPTIONS = [
  { value: "account", label: "Hesap ve giriş" },
  { value: "technical", label: "Teknik sorun" },
  { value: "booking", label: "Rezervasyon" },
  { value: "privacy", label: "Gizlilik / veri talebi" },
  { value: "feedback", label: "Öneri ve geri bildirim" },
  { value: "other", label: "Diğer" },
];

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  category: "account",
  subject: "",
  message: "",
};

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export default function SupportRequestForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const set = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
    setErrors((current) => ({ ...current, [key]: "" }));
    setFeedback(null);
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.name.trim()) nextErrors.name = "Ad soyad gerekli.";
    if (!form.email.trim()) nextErrors.email = "E-posta gerekli.";
    else if (!isValidEmail(form.email.trim())) nextErrors.email = "Geçerli bir e-posta girin.";
    if (!form.message.trim()) nextErrors.message = "Mesaj gerekli.";
    else if (form.message.trim().length < 10) nextErrors.message = "Mesaj en az 10 karakter olmalı.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);
      const categoryLabel = CATEGORY_OPTIONS.find((item) => item.value === form.category)?.label || "Destek";
      supportRequestsRepo.create({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        category: form.category,
        subject: form.subject.trim() || categoryLabel,
        message: form.message.trim(),
        status: "open",
        source: "web",
      });
      setForm(EMPTY_FORM);
      setFeedback({
        tone: "success",
        message: "Talebiniz alındı. Ekibimiz 1–3 iş günü içinde e-posta adresinizden size dönüş yapacaktır.",
      });
    } catch (submitError) {
      setFeedback({
        tone: "danger",
        message: submitError?.message || "Talep gönderilemedi. Lütfen tekrar deneyin veya destek e-postasını kullanın.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-5">
      <h2 className="h3 mb-3">Destek talebi gönder</h2>
      <p className="text-muted mb-4">
        Formu doldurarak talebinizi doğrudan yönetim ekibine iletebilirsiniz. Talepler admin panelinde görüntülenir ve
        yanıt için kayıtlı e-posta adresiniz kullanılır.
      </p>

      {feedback ? (
        <Alert color={feedback.tone === "success" ? "success" : "danger"} className="mb-4">
          {feedback.message}
        </Alert>
      ) : null}

      <form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="support-name">Ad soyad</Label>
              <Input id="support-name" value={form.name} onChange={set("name")} invalid={!!errors.name} />
              <FormFeedback>{errors.name}</FormFeedback>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="support-email">E-posta</Label>
              <Input
                id="support-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                invalid={!!errors.email}
              />
              <FormFeedback>{errors.email}</FormFeedback>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="support-phone">Telefon (opsiyonel)</Label>
              <Input id="support-phone" value={form.phone} onChange={set("phone")} placeholder="+90 5xx xxx xx xx" />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="support-category">Konu</Label>
              <Input id="support-category" type="select" value={form.category} onChange={set("category")}>
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        <FormGroup>
          <Label for="support-subject">Başlık (opsiyonel)</Label>
          <Input
            id="support-subject"
            value={form.subject}
            onChange={set("subject")}
            placeholder="Örn. Giriş yapamıyorum"
          />
        </FormGroup>

        <FormGroup>
          <Label for="support-message">Mesajınız</Label>
          <Input
            id="support-message"
            type="textarea"
            rows={5}
            value={form.message}
            onChange={set("message")}
            invalid={!!errors.message}
            placeholder="Sorununuzu veya talebinizi mümkün olduğunca ayrıntılı yazın."
          />
          <FormFeedback>{errors.message}</FormFeedback>
        </FormGroup>

        <Button color="info" type="submit" disabled={submitting}>
          {submitting ? "Gönderiliyor..." : "Talebi gönder"}
        </Button>
      </form>
    </section>
  );
}
